import React, { useState, useEffect } from 'react';
import { HardDrive, Activity, TrendingUp, Info, Cpu, Zap, Signal } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function AdminDashboard({ token, status, netSpeed }) {
  console.log("SYSTEM_STATUS_RECEIVED:", status);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (status) {
      setHistory(prev => {
        const parsedRam = parseFloat(status.memory?.percent);
        const ramVal = isNaN(parsedRam) ? 0 : parsedRam;
        const newHistory = [...prev, {
          time: new Date().toLocaleTimeString().split(' ')[0],
          cpu: parseFloat((status.load?.[0] || 0) * 20), // Scaled for graph
          ram: ramVal
        }].slice(-20); // Keep last 20 points
        return newHistory;
      });
    }
  }, [status]);

  if (!status) return <div className="p-20 text-center font-black text-slate-500 animate-pulse">Serverdan ma'lumotlar kutilmoqda...</div>;

  const parsedRam = parseFloat(status.memory?.percent);
  const ramPercent = isNaN(parsedRam) ? 0 : parsedRam;

  const parsedSwap = parseFloat(status.memory?.swapPercent);
  const swapPercent = isNaN(parsedSwap)
    ? (status.memory?.swapTotal ? Math.round((status.memory.swapUsed / status.memory.swapTotal) * 100) : 0)
    : parsedSwap;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* 4 Large Stats Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatusCard icon={<Cpu/>} title="CPU Load" value={`${(status.load?.[0] || 0).toFixed(2)}`} sub={`Core temp: ${status.temp || 0}°C`} color="indigo" />
        <StatusCard
          icon={<HardDrive/>}
          title="RAM Percent"
          value={`${ramPercent}%`}
          sub={`${status.memory?.used ? (status.memory.used / 1024**3).toFixed(1) : "0.0"}GB / ${status.memory?.total ? (status.memory.total / 1024**3).toFixed(1) : "0.0"}GB`}
          color="blue"
        />
        <StatusCard
          icon={<Zap/>}
          title="Swap Memory"
          value={`${swapPercent}%`}
          sub={`${status.memory?.swapUsed ? (status.memory.swapUsed / 1024**3).toFixed(1) : "0.0"}GB / ${status.memory?.swapTotal ? (status.memory.swapTotal / 1024**3).toFixed(1) : "0.0"}GB`}
          color="indigo"
        />
        <StatusCard icon={<Signal/>} title="Network Speed" value={`${netSpeed.rx} KB/s`} sub={`TX: ${netSpeed.tx} KB/s`} color="emerald" />
        <StatusCard icon={<Clock/>} title="System Uptime" value={status.uptime} sub="Server barqaror ishlamoqda" color="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Resource Graph */}
        <div className="bg-slate-900 p-10 rounded-[45px] border border-blue-500/10 shadow-2xl">
          <h3 className="text-xl font-black mb-10 flex items-center gap-3 text-white"><Activity className="text-blue-400"/> Resurslar Monitoringi</h3>
          <div className="h-[300px]">
             <ResponsiveContainer width="100%" height="100%">
               <AreaChart data={history}>
                  <defs>
                    <linearGradient id="colorRam" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff05" />
                  <XAxis dataKey="time" hide />
                  <YAxis hide domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{backgroundColor: '#020617', borderRadius: '20px', border: '1px solid #1e293b'}}
                  />
                  <Area type="monotone" dataKey="ram" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorRam)" name="RAM %" />
                  <Area type="monotone" dataKey="cpu" stroke="#fbbf24" strokeWidth={3} fill="transparent" name="CPU x20" />
               </AreaChart>
             </ResponsiveContainer>
          </div>
        </div>

        {/* Disk Info */}
        <div className="bg-slate-900 p-10 rounded-[45px] border border-blue-500/10 shadow-2xl flex flex-col justify-center">
            <h3 className="text-xl font-black mb-6 flex items-center gap-3 text-white"><HardDrive className="text-indigo-400"/> Disk Ma'lumotlari</h3>
            <div className="space-y-6">
                <div className="flex justify-between items-end">
                    <span className="text-slate-400 font-bold uppercase text-xs tracking-widest">Disk Bandligi: {status.disk?.percent}</span>
                    <span className="text-white font-black text-2xl">{status.disk?.used} / {status.disk?.total}</span>
                </div>
                <div className="w-full h-4 bg-slate-950 rounded-full overflow-hidden border border-white/5">
                    <div className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 shadow-[0_0_15px_rgba(59,130,246,0.5)]" style={{width: status.disk?.percent}} />
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4">
                    <div className="p-5 bg-white/5 rounded-2xl border border-white/5">
                        <p className="text-[10px] font-black text-slate-500 uppercase mb-1">Mavjud Joy</p>
                        <p className="text-lg font-black text-emerald-400">{status.disk?.available}</p>
                    </div>
                    <div className="p-5 bg-white/5 rounded-2xl border border-white/5">
                        <p className="text-[10px] font-black text-slate-500 uppercase mb-1">Status</p>
                        <p className="text-lg font-black text-blue-400">Normal</p>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ icon, title, value, sub, color }) {
  const colors = {
    indigo: "text-indigo-400 border-indigo-500/20",
    blue: "text-blue-400 border-blue-500/20",
    emerald: "text-emerald-400 border-emerald-500/20",
    orange: "text-orange-400 border-orange-500/20"
  };
  return (
    <div className={`bg-slate-900 p-8 rounded-[40px] border ${colors[color]} shadow-2xl`}>
      <div className="flex justify-between items-start mb-4">
        <div className="p-4 bg-white/5 rounded-2xl">{React.cloneElement(icon, {size: 32})}</div>
        <span className="text-3xl font-black text-white">{value}</span>
      </div>
      <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">{title}</p>
      <p className="text-xs font-bold text-slate-400 mt-1 opacity-70">{sub}</p>
    </div>
  );
}

const Clock = ({size}) => <Info size={size} />; // Simple wrapper if Lucide Clock is missing

export default AdminDashboard;
